import React, { useEffect, useMemo, useState } from "react";
import { Button, Center, Group, Loader, Select, Switch, Text, TextInput, Title, useMantineColorScheme, Container } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { useListFranchisesQuery, useAddTeamMutation } from "generated/graphql";

/**
 * TeamCreate
 *
 * Admin page to create a new team.
 * Uses ListFranchises query to populate the franchise dropdown
 * and AddTeam mutation to create the team.
 */
export default function TeamCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Get pre-selected franchise from URL params (e.g., ?franchiseId=xxx)
  const preSelectedFranchiseId = searchParams.get("franchiseId");

  // Fetch all franchises for the dropdown (MaxPageSize is 50)
  const { data: franchisesData, loading: franchisesLoading } = useListFranchisesQuery({
    variables: { take: 50 },
    fetchPolicy: "cache-and-network",
  });

  // Mutation to create team
  const [addTeam, { loading: creating }] = useAddTeamMutation();

  // Form state
  const [name, setName] = useState("");
  const [franchiseId, setFranchiseId] = useState<string | null>(preSelectedFranchiseId);
  const [active, setActive] = useState<boolean>(true);

  // Pre-select franchise when data loads (if coming from FranchiseView)
  useEffect(() => {
    if (preSelectedFranchiseId && franchisesData?.franchises?.items) {
      const franchiseExists = franchisesData.franchises.items.some(
        (f) => f.id === preSelectedFranchiseId && f.active
      );
      if (franchiseExists) {
        setFranchiseId(preSelectedFranchiseId);
      }
    }
  }, [preSelectedFranchiseId, franchisesData]);

  // Map franchises to select options
  const franchiseOptions = useMemo(() => {
    return (franchisesData?.franchises?.items ?? [])
      .filter((f) => f.active) // Only show active franchises
      .map((f) => ({
        value: f.id,
        label: f.prefix ? `[${f.prefix}] ${f.name}` : f.name,
      }));
  }, [franchisesData]);

  async function handleCreate() {
    if (!name.trim() || !franchiseId) return;

    try {
      const result = await addTeam({
        variables: {
          franchiseId: franchiseId,
          teamName: name.trim(),
        },
      });

      if (result.data?.addTeam) {
        notifications.show({
          title: "Success",
          message: `Team "${result.data.addTeam.name}" created successfully`,
          color: "green",
        });
        navigate("/admin-hub/teams");
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to create team",
          color: "red",
        });
      }
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "An error occurred while creating the team",
        color: "red",
      });
    }
  }

  return (
    <Container size="xl" px="lg" py="lg">
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub/teams")}
          size="sm"
        >
          Back to Teams
        </Button>
      </Group>
      <div className="space-y-4 max-w-2xl">
        <Title c={isDark ? "white" : "black"}>Create Team</Title>
        <Text c={isDark ? "gray.4" : "gray.7"}>
          Create a new team under an existing franchise.
        </Text>

        <div className="space-y-4">
          <TextInput
            label="Team Name"
            placeholder="Enter team name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            withAsterisk
            autoComplete="off"
          />

          <Select
            label="Franchise"
            placeholder={franchisesLoading ? "Loading franchises..." : "Select franchise"}
            data={franchiseOptions}
            value={franchiseId}
            onChange={setFranchiseId}
            withAsterisk
            searchable
            disabled={franchisesLoading}
            nothingFoundMessage="No franchises found"
            rightSection={franchisesLoading ? <Loader size="xs" /> : undefined}
          />

          <Group align="center">
            <Switch
              checked={active}
              onChange={(e) => setActive(e.currentTarget.checked)}
              label="Active"
              disabled
            />
            <Text size="xs" c={isDark ? "gray.5" : "gray.6"}>
              (Teams are created as active by default)
            </Text>
          </Group>

          <Group mt="md">
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleCreate}
              disabled={!name.trim() || !franchiseId || creating}
              loading={creating}
            >
              Create Team
            </Button>
            <Button variant="light" color="gray" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Group>
        </div>
      </div>
    </Container>
  );
}
